import { ApplicationRoleCode, ICurrentUserResponse } from '@kcftech/prometheus-client';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { hasAccessToMultipleCorporations } from 'contexts/UserContext';
import { DefaultIssueMode } from 'pages/Desk/issues/shared';
import { shouldRenderSettingsRoute } from 'pages/Settings/SettingsPage';
import { useLocation } from 'react-router-dom';
import { findLastIndex } from 'utils/array';
import { Environment } from 'utils/EnvironmentHelper';
import { Lookup } from 'utils/types';
import { isValidGuid } from 'utils/validation';

export enum ModuleRoutes {
    MissionControl = 'mission-control',
    Workbench = 'workbench',
    Dashboards = 'dashboards',
    Desk = 'desk',
    Settings = 'settings',
    Map = 'maps',
    Models = 'models',
    Hardware = 'hardware',
    Reports = 'reports',
    Demo = 'demo',
    Error = 'error',
}

export function getCurrentEnvironment() {
    if (Environment.cognito.Domain.includes('sdstage01')) {
        return 'sdstage01';
    }
    if (Environment.cognito.Domain.includes('sdstage02')) {
        return 'sdstage02';
    }
    if (Environment.cognito.Domain.includes('sddev04')) {
        return 'sddev04';
    }
    return 'sd';
}

export function getMissionControlRoute(
    corporationId?: string,
    locationId?: string,
): string {
    if (locationId) {
        return `/location/${locationId}/mission-control`;
    }
    if (corporationId) {
        return `/corporation/${corporationId}/mission-control`;
    }
    return '/mission-control';
}

export function getOverviewLandingPageRoute(): string {
    return '/';
}

export function getCorporationLandingPageRoute(
    corporationId: string | undefined,
): string {
    return `/corporation/${corporationId}`;
}

export function getLocationLandingPageRoute(locationId: string | undefined): string {
    return `/location/${locationId}`;
}

export function getLocationDashboardBaseRoute(locationId: string | undefined): string {
    return `/location/${locationId}/dashboards`;
}

export function getCorporationDashboardBaseRoute(
    corporationId: string | undefined,
): string {
    return `/corporation/${corporationId}/dashboards`;
}

export function getCorporationSpecificDashboardRoute(
    corporationId: string | undefined,
    dashboardId: string | undefined,
): string {
    return `/corporation/${corporationId}/dashboards/${dashboardId}`;
}

export function getLocationSpecificDashboardRoute(
    locationId: string | undefined,
    dashboardId: string | undefined,
): string {
    return `/location/${locationId}/dashboards/${dashboardId}`;
}

export function getLocationSettingsBaseRoute(locationId: string | undefined): string {
    return `/location/${locationId}/settings`;
}

export function getLocationReportsBaseRoute(locationId: string | undefined): string {
    return `/location/${locationId}/reports`;
}

export function getLocationSettingsRoute(locationId: string, tabs?: string[]): string {
    // if navigating to location settings from corporation settings on the locations tab, tab needs to be made singular
    if (tabs) {
        const rootSettingsSection = tabs[0];
        if (rootSettingsSection === 'locations') {
            return `${getLocationSettingsBaseRoute(locationId)}/location`;
        }

        if (shouldRenderSettingsRoute(rootSettingsSection, 'Location')) {
            if (tabs.some((v) => v.toLowerCase() === 'filelibrary')) {
                return `${getLocationSettingsBaseRoute(
                    locationId,
                )}/${rootSettingsSection}`;
            }
            return `${getLocationSettingsBaseRoute(locationId)}/${tabs.join('/')}`;
        }
    }
    return getLocationSettingsBaseRoute(locationId);
}

export function getLocationReportsRoute(locationId: string, tab?: string): string {
    return tab
        ? `${getLocationReportsBaseRoute(locationId)}/${tab}`
        : getLocationReportsBaseRoute(locationId);
}

export function getCorporationSettingsRoute(
    corporationId: string,
    tabs?: string[],
): string {
    if (tabs) {
        const rootSettingsSection = tabs[0];
        // if we are navigating from the global corporations tab, tab needs to be made singular
        if (rootSettingsSection === 'corporations') {
            return `${getCorporationSettingsBaseRoute(corporationId)}/corporation`;
        }
        if (rootSettingsSection === 'location') {
            return `${getCorporationSettingsBaseRoute(corporationId)}/locations`;
        }

        if (shouldRenderSettingsRoute(rootSettingsSection, 'Corporation')) {
            return `${getCorporationSettingsBaseRoute(corporationId)}/${tabs.join('/')}`;
        }
    }

    return getCorporationSettingsBaseRoute(corporationId);
}

export function getCorporationReportsRoute(corporationId: string, tab?: string): string {
    return tab
        ? `${getCorporationReportsBaseRoute(corporationId)}/${tab}`
        : getCorporationReportsBaseRoute(corporationId);
}

export function getCorporationSettingsBaseRoute(
    corporationId: string | undefined,
): string {
    if (corporationId) {
        return `/corporation/${corporationId}/settings`;
    }
    return '/';
}

export function getCorporationReportsBaseRoute(
    corporationId: string | undefined,
): string {
    if (corporationId) {
        return `/corporation/${corporationId}/reports`;
    }
    return '/';
}

export function getGlobalSettingsRoute(tabs?: string[]): string {
    if (tabs) {
        if (
            tabs.some((tab) =>
                ['filelibrary', 'integrations'].includes(tab.toLowerCase()),
            )
        ) {
            return `/settings`;
        }
        if (tabs[0] === 'corporation') {
            return `/settings/corporations`;
        }
        if (tabs[0] === 'location') {
            return `/settings/locations`;
        }
        if (!shouldRenderSettingsRoute(tabs[0], 'Global')) {
            return `/settings`;
        }
        return `/settings/${tabs.join('/')}`;
    }
    return `/settings`;
}

export function getGlobalReportsRoute(tab?: string): string {
    if (tab) {
        return `/reports/${tab}`;
    }
    return `/reports`;
}

export function getCorporationSettingsDefaultRoute(
    corporationId: string | undefined,
): string {
    if (corporationId) {
        return `${getCorporationSettingsBaseRoute(corporationId)}`;
    }
    return '/';
}

export function getCorporationModelRoute(
    corporationId: string | undefined,
    tab: string,
    modelId?: string,
): string {
    if (corporationId) {
        return `/corporation/${corporationId}${getBaseModelRoute(tab, modelId)}`;
    }
    return '/';
}

export function getLocationModelRoute(
    locationId: string | undefined,
    tab: string,
    modelId?: string,
): string {
    if (locationId) {
        return `/location/${locationId}${getBaseModelRoute(tab, modelId)}`;
    }
    return '/';
}

export function getCorporationModelDefaultRoute(
    corporationId: string | undefined,
): string {
    if (corporationId) {
        return `/corporation/${corporationId}${getModelDefaultRoute()}`;
    }
    return '/';
}

export function getLocationModelDefaultRoute(locationId: string | undefined): string {
    if (locationId) {
        return `/location/${locationId}${getModelDefaultRoute()}`;
    }
    return '/';
}

export function getBaseModelRoute(tab: string, modelId?: string): string {
    const route = `/settings/models/${tab}`;
    if (modelId) {
        return `${route}/${modelId}`;
    }
    return route;
}

export function getModelDefaultRoute(): string {
    return `/settings/models/repository`;
}

export type HardwareOverviewTab =
    | 'repeaters'
    | 'baseStations'
    | 'sensors'
    | 'hubs'
    | 'piezo';

export type HardwareOverviewSort = {
    field: string;
    direction: 'asc' | 'desc';
};

export function getHardwareDetailsRoute(
    locationId: string,
    tab: HardwareOverviewTab,
    id: number,
) {
    return `${getHardwareOverviewRoute(locationId, tab)}/${id}`;
}

export function getHardwareOverviewRoute(
    locationId: string,
    tab: HardwareOverviewTab,
    sort?: HardwareOverviewSort,
): string {
    const tabs = ['overview', tab];
    const baseRoute = getHardwareRoute(undefined, locationId, tabs);

    if (sort) {
        const params = new URLSearchParams();
        params.set('sortField', sort.field);
        params.set('sortDir', sort.direction);
        return `${baseRoute}?${params.toString()}`;
    }

    return baseRoute;
}

export function getHardwareAdminRoute(
    locationId: string | undefined,
    tab: HardwareOverviewTab,
    id: number | undefined,
): string {
    const tabs = ['adminSetup', tab];
    if (id !== undefined) {
        tabs.push(id.toString());
    }
    return getHardwareRoute(undefined, locationId, tabs);
}

export function getHardwareRoute(
    corporationId?: string,
    locationId?: string,
    tabs: string[] = [],
) {
    const baseRoute = '/hardware/';
    if (locationId) {
        return `/location/${locationId}${baseRoute}${tabs.join('/')}`;
    }
    if (corporationId) {
        return `/corporation/${corporationId}${baseRoute}${tabs.join('/')}`;
    }

    // Otherwise, go to base hardware route at the global level which will default to adminSetup
    // We don't utilize the tabs here to prevent errors for users that do not have access to certain tabs (Sentry and below)
    return `${baseRoute}`;
}

export function getSDBaseLink(): string {
    return `https://v3.${getCurrentEnvironment()}.kcftech.com/`;
}

export function getSDCorporationMapLink(corporationId: string | undefined): string {
    if (corporationId) {
        return `https://v3.${getCurrentEnvironment()}.kcftech.com/#/corporation/${corporationId}/map/Main`;
    }
    return `https://v3.${getCurrentEnvironment()}.kcftech.com/`;
}

export function getSDLocationMapLink(locationId: string | undefined): string {
    if (locationId) {
        return `https://v3.${getCurrentEnvironment()}.kcftech.com/#/account/${locationId}/map/Main`;
    }
    return `https://v3.${getCurrentEnvironment()}.kcftech.com/`;
}

export function getLocationWorkbenchBaseRoute(locationId: string): string {
    return `/location/${locationId}/workbench`;
}

export enum WorkbenchTab {
    Trend = 'trend',
    Assets = 'assets',
    Indicators = 'indicators',
    MonitoringPoints = 'monitoringpoints',
    ActivityLog = 'activitylog',
}

export enum WorkbenchTrendUrlParam {
    Session = 'sessionId',
    Indicator = 'indicatorId',
    Issue = 'issueId',
    ChartData = 'chartDataId',
    Group = 'groupId',
    Alert = 'alertId',
    ActivityEvent = 'activityEventId',
    StartDate = 'startDate',
    IndicatorTypes = 'indicatorTypes',
    Action = 'action',
}

export enum WorkbenchTrendUrlAction {
    Acknowledge = 'acknowledge',
}

type TrendQueryParam = WorkbenchTrendUrlParam;

export type TrendQueryParams = Partial<Lookup<TrendQueryParam, string | undefined>>;

function toUrlParams(url: string | URL | URLSearchParams): URLSearchParams {
    if (typeof url === 'string') {
        return new URL(url).searchParams;
    }
    if (url instanceof URL) {
        return url.searchParams;
    }
    if (url instanceof URLSearchParams) {
        return url;
    }
    throw Error('Invalid type provided');
}

export function extractTrendUrlParams(
    url: string | URL | URLSearchParams,
): TrendQueryParams {
    const params = toUrlParams(url);
    return cleanWorkbenchParams({
        sessionId: params.get(WorkbenchTrendUrlParam.Session) ?? undefined,
        indicatorId: params.get(WorkbenchTrendUrlParam.Indicator) ?? undefined,
        issueId: params.get(WorkbenchTrendUrlParam.Issue) ?? undefined,
        chartDataId: params.get(WorkbenchTrendUrlParam.ChartData) ?? undefined,
        groupId: params.get(WorkbenchTrendUrlParam.Group) ?? undefined,
        alertId: params.get(WorkbenchTrendUrlParam.Alert) ?? undefined,
        activityEventId: params.get(WorkbenchTrendUrlParam.ActivityEvent) ?? undefined,
        startDate: params.get(WorkbenchTrendUrlParam.StartDate) ?? undefined,
        indicatorTypes: params.get(WorkbenchTrendUrlParam.IndicatorTypes) ?? undefined,
        action: params.get(WorkbenchTrendUrlParam.Action) ?? undefined,
    });
}

/**
 * Some of our workbench URL params are mutually exclusive with each other. This
 * removes the extraneous query params.
 *
 * For example, if you're loading state from an indicator, that'll take
 * precedence over a group (the groupId will be removed, the indicatorId will
 * be kept).
 */
export function cleanWorkbenchParams(params: TrendQueryParams): TrendQueryParams {
    // separate the mutually exclusive params from the rest
    const {
        sessionId,
        alertId,
        activityEventId,
        indicatorId,
        groupId,
        issueId,
        chartDataId,
        ...rest
    } = params;

    const mutuallyExclusiveParam = (() => {
        if (sessionId) {
            return { sessionId };
        }
        if (alertId) {
            return { alertId };
        }
        if (activityEventId) {
            return { activityEventId };
        }
        if (indicatorId) {
            return { indicatorId };
        }
        if (chartDataId) {
            return { chartDataId, issueId };
        }
        if (issueId) {
            return { issueId };
        }
        if (groupId) {
            return { groupId };
        }
        return undefined;
    })();
    return {
        ...rest,
        ...mutuallyExclusiveParam,
    };
}

export function getSystemWorkbenchDefaultRoute(systemId: string): string {
    return getSystemWorkbenchTab(systemId, WorkbenchTab.Trend);
}

export function getLocationWorkbenchGroupRoute(
    locationId: string,
    systemId: string,
    groupId?: string,
): string {
    const baseRoute = `${getLocationWorkbenchBaseRoute(locationId)}/${systemId}/${
        WorkbenchTab.Trend
    }`;
    if (groupId) {
        return `${baseRoute}?groupId=${groupId}`;
    }
    return baseRoute;
}

/**
 * Returns full path to a specific area in the workbench.
 */
export function getSystemWorkbenchTab(
    systemId: string,
    tab: WorkbenchTab,
    groupId?: string,
    filter?: string,
): string {
    const baseRoute = `/system/${systemId}/workbench/${tab}`;
    if (groupId) {
        const filterParam = filter ? `&${filter}` : '';
        return `${baseRoute}?groupId=${groupId}${filterParam}`;
    }
    return baseRoute;
}

export function getSystemWorkbenchUrl(
    systemId: string,
    tab: WorkbenchTab,
    params: TrendQueryParams,
): string {
    const baseRoute = `/system/${systemId}/workbench/${tab}`;

    const queryString = new URLSearchParams(
        Object.entries(cleanWorkbenchParams(params))
            .filter((param) => !!param[1])
            .sort((a, b) => a[0].localeCompare(b[0])),
    ).toString();

    return baseRoute + (queryString ? `?${queryString}` : '');
}

export function getAssetDetailsRoute(systemId: string, assetId: string): string {
    return `${getSystemWorkbenchTab(systemId, WorkbenchTab.Assets)}/${assetId}`;
}

export function getAssetImagesRoute(
    systemId: string,
    assetId: string,
    imageId?: string,
): string {
    const base = `${getAssetDetailsRoute(systemId, assetId)}/images`;
    return imageId ? `${base}?imageId=${imageId}` : base;
}

export function getIssuesPageRoute(
    locationId: string,
    mode = DefaultIssueMode,
    tab: string,
) {
    return `${getLocationDeskBaseRoute(locationId)}${mode}/${tab}`;
}

export function getIssueDetailsRoute(
    locationId: string,
    issueId: string,
    tabId?: string,
) {
    return `${getLocationDeskBaseRoute(locationId)}details/${issueId}/${tabId || ''}`;
}

export function getIssueActivityRoute(
    locationId: string,
    issueId: string,
    activityId?: string,
) {
    const url = getIssueDetailsRoute(locationId, issueId);
    return activityId ? `${url}?activityId=${activityId}` : url;
}

export function getIssueTasksRoute(locationId: string, issueId: string, taskId?: string) {
    const url = getIssueDetailsRoute(locationId, issueId, 'tasks');
    return taskId ? `${url}?taskId=${taskId}` : url;
}

export function getLocationDeskBaseRoute(locationId: string): string {
    return `/location/${locationId}/desk/issues/`;
}

export function getLocationMachineHealthDeskBaseRoute(
    locationId: string,
    tab: string,
    faultTypeId?: string,
    groupId?: string,
    status?: string,
): string {
    const params = `?${faultTypeId ? `faultTypeId=${faultTypeId}` : ''}${
        groupId ? `${faultTypeId ? '&' : ''}groupId=${groupId}` : ''
    }${status ? `${faultTypeId || groupId ? '&' : ''}status=${status}` : ''}`;

    return `${getLocationDeskBaseRoute(locationId)}machineHealth/${tab}${params}`;
}

export function getLocationHardwareHealthDeskBaseRoute(
    locationId: string,
    tab: string,
): string {
    return `${getLocationDeskBaseRoute(locationId)}hardwareHealth/${tab}`;
}

export function getDeskRoute(locationId: string, tabs: string[] = []) {
    const baseRoute = getLocationDeskBaseRoute(locationId);
    return `${baseRoute}${tabs.join('/')}`;
}

export function getLocationFileLibraryBaseRoute(
    locationId: string,
    fileId: string,
): string {
    return `/location/${locationId}/settings/fileLibrary/f/${fileId}`;
}

export function getSettingsBaseRoute(currentUrl: string): string {
    const pattern = 'settings';
    const settingLoc = currentUrl.indexOf(pattern);
    return currentUrl.substr(0, settingLoc + pattern.length);
}

export function getSingleLocationRoute(locationId: string): string {
    return `${getLocationSettingsBaseRoute(locationId)}/location`;
}

export function getSingleCorporationRoute(corporationId: string): string {
    return `${getCorporationSettingsBaseRoute(corporationId)}/corporation`;
}

function getMapBaseRoute(corporationId?: string, locationId?: string, systemId?: string) {
    if (systemId) {
        return `/system/${systemId}/maps`;
    }
    if (locationId) {
        return `/location/${locationId}/maps`;
    }
    if (corporationId) {
        return `/corporation/${corporationId}/maps`;
    }
    throw new Error('Can not determine map route');
}

export function getMapsTableRoute(
    corporationId?: string,
    locationId?: string,
    systemId?: string,
    groupId?: string,
) {
    const baseUrl = getMapBaseRoute(corporationId, locationId, systemId);
    if (groupId) {
        return `${baseUrl}/table?groupId=${groupId}`;
    }
    return `${baseUrl}/table`;
}

export function getMapsRoute(
    corporationId?: string,
    locationId?: string,
    systemId?: string,
    groupId?: string,
    mapId?: string,
) {
    let baseUrl = getMapBaseRoute(corporationId, locationId, systemId);
    if (mapId) {
        baseUrl = `${baseUrl}/${mapId}`;
    }
    if (groupId) {
        return `${baseUrl}?groupId=${groupId}`;
    }
    return baseUrl;
}

/**
 * Returns partial path to a tab in the workbench, but without the location workbench prefix.
 * That is, in order to route to the tab you must manually add the workbench URL.
 */
export const getLocationWorkbenchTabPath = (tab: WorkbenchTab): string => `/${tab}`;

export function getDashboardFavoritesLink(
    locationId: string | undefined,
    corporationId: string | undefined,
): string {
    return locationId
        ? getLocationDashboardBaseRoute(locationId)
        : getCorporationDashboardBaseRoute(corporationId);
}

export function getDashboardSpecificFavoritesLink(
    dashboardId: string,
    locationId: string | undefined,
    corporationId: string | undefined,
): string {
    return locationId
        ? getLocationSpecificDashboardRoute(locationId, dashboardId)
        : getCorporationSpecificDashboardRoute(corporationId, dashboardId);
}

export function getIntegrationsLink(locationId: string, integrationId?: string): string {
    const baseRoute = `/location/${locationId}/settings/integrations`;
    return integrationId ? `${baseRoute}/${integrationId}` : baseRoute;
}

export function getTabId(url: string): string | undefined {
    const tabId = url.split('/').pop();
    return tabId;
}

export function isUserInternal(role?: ApplicationRoleCode): boolean {
    return isUserGlobalAdminOrSentry(role) || role === ApplicationRoleCode.Sales;
}

export function isUserGlobalAdminOrSentry(role?: ApplicationRoleCode): boolean {
    return isUserGlobalAdmin(role) || role === ApplicationRoleCode.Sentry;
}

export function isUserGlobalAdmin(role?: ApplicationRoleCode): boolean {
    return role === ApplicationRoleCode.GlobalAdmin;
}

const NumberOrSerialNumberUrlPartRegex = /([0-9]+|-[0-9a-fA-F]+)/;

function endsWithHardwareIdentifier(pathname: string): boolean {
    const lastSlashIdx = pathname.lastIndexOf('/');
    const lastPart = pathname.substring(lastSlashIdx + 1);
    return NumberOrSerialNumberUrlPartRegex.test(lastPart);
}

/**
 * Resolves the closest appropriate hardware route based on pathname and context.
 * The scope (location vs corporation) is inferred from which ID is provided.
 *
 * @param pathname The current URL pathname
 * @param options.corporationId The corporation ID (mutually exclusive with locationId)
 * @param options.locationId The location ID (mutually exclusive with corporationId)
 * @returns The resolved hardware route
 */
const HARDWARE_TABS_ONLY_AT_LOCATION_LEVEL: readonly string[] = ['overview', 'alertLog'];

function resolveClosestHardwareRoute(
    pathname: string,
    options: { corporationId?: string; locationId?: string },
): string {
    const baseTabs = getTabs(pathname, ModuleRoutes.Hardware);
    const isLocationScope = !!options.locationId;

    // If the last portion of the URL is a hardware number, we're on a details page
    // and need to prepend the appropriate context tab.
    const tabs =
        endsWithHardwareIdentifier(pathname) && baseTabs
            ? [isLocationScope ? 'overview' : 'adminSetup', baseTabs[0]]
            : baseTabs;

    /*
     * Discard the entire tab path if any segment is location-only.
     * Tabs are URL path segments (e.g., ['overview', '123']), not sibling tab IDs,
     * so a location-only first segment invalidates the whole path at corp/global scope.
     */
    const filteredTabs =
        !isLocationScope &&
        tabs &&
        tabs.some((tab) => HARDWARE_TABS_ONLY_AT_LOCATION_LEVEL.includes(tab))
            ? undefined
            : tabs;

    // Current URL's tab path contains a location-only segment; redirect to landing page
    // instead of rendering an empty or invalid hardware page
    if (!isLocationScope && tabs && !filteredTabs) {
        return options.corporationId
            ? getCorporationLandingPageRoute(options.corporationId)
            : getOverviewLandingPageRoute();
    }

    // Any location-only tab paths at corp/global scope have been redirected above.
    return getHardwareRoute(options.corporationId, options.locationId, filteredTabs);
}

export const determineLocationNavigationRoute = (
    pathname: string,
    locationId: string,
    isPremium: boolean,
) => {
    // Basic route checks
    if (!isPremium) {
        if (pathname.includes(ModuleRoutes.Settings)) {
            return getLocationSettingsRoute(
                locationId,
                getTabs(pathname, ModuleRoutes.Settings),
            );
        }
        if (pathname.includes(ModuleRoutes.Reports)) {
            return getLocationReportsBaseRoute(locationId);
        }
        if (pathname.includes(ModuleRoutes.Hardware)) {
            return resolveClosestHardwareRoute(pathname, { locationId });
        }

        // Default for Basic Corporations is to arrive at the hardware page when selecting a location
        // need double check in order to navigate to a specific tab if there is one
        return getHardwareRoute(undefined, locationId, undefined);
    }

    if (pathname.includes(ModuleRoutes.Workbench)) {
        return getLocationWorkbenchBaseRoute(locationId);
    }
    if (pathname.includes(ModuleRoutes.Dashboards)) {
        return getLocationDashboardBaseRoute(locationId);
    }
    if (pathname.includes(ModuleRoutes.Desk)) {
        if (pathname.includes('details')) {
            return getLocationMachineHealthDeskBaseRoute(locationId, 'active');
        }

        return getDeskRoute(locationId, getTabs(pathname, 'issues'));
    }
    if (pathname.includes(ModuleRoutes.Settings)) {
        return getLocationSettingsRoute(
            locationId,
            getTabs(pathname, ModuleRoutes.Settings),
        );
    }
    if (pathname.includes(ModuleRoutes.Map)) {
        return getMapsRoute(undefined, locationId);
    }
    if (pathname.includes(ModuleRoutes.Hardware)) {
        return resolveClosestHardwareRoute(pathname, { locationId });
    }
    if (pathname.includes(ModuleRoutes.Reports)) {
        return getLocationReportsBaseRoute(locationId);
    }
    return getLocationLandingPageRoute(locationId);
};

const CorporateDashboardRegex =
    /dashboards\/(deployment-and-value|desk-activity-overview|value-by-location)\/?$/;

/**
 * For url paths pointing to a static (non-PowerBI/user-created) corporate
 * dashboard. (i.e. one built-in to sd), returns the url fragment denoting that
 * dashboard. Otherwise, returns null.
 */
function getStaticCorporateDashboardSlug(path: string) {
    const result = CorporateDashboardRegex.exec(path);
    if (!result) {
        return null;
    }
    return result[1];
}

export function useIsDashboardView() {
    const routeLocation = useLocation();

    return /dashboards\/(deployment-and-value|desk-activity-overview|value-by-location)/.test(
        routeLocation.pathname,
    );
}

export const determineCorporationNavigationRoute = (
    pathname: string,
    corporationId: string,
    isPremium: boolean,
    permissions: PermissionsContext,
) => {
    // Basic route checks
    if (!isPremium) {
        if (pathname.includes(ModuleRoutes.Settings)) {
            return getCorporationSettingsRoute(
                corporationId,
                getTabs(pathname, ModuleRoutes.Settings),
            );
        }
        if (pathname.includes(ModuleRoutes.Reports)) {
            return getCorporationReportsBaseRoute(corporationId);
        }
        if (
            pathname.includes(ModuleRoutes.Hardware) &&
            pathname.split(`${ModuleRoutes.Hardware}/`).length > 1
        ) {
            return resolveClosestHardwareRoute(pathname, { corporationId });
        }
        // Default landing for basic corporations is the Hardware page, need double check here for case where we are not already on a tab
        return `${getHardwareRoute(corporationId, undefined, undefined)}`;
    }
    if (
        permissions.canViewCorporationDashboards &&
        pathname.includes(ModuleRoutes.Dashboards)
    ) {
        const staticCorporateDashboardSlug = getStaticCorporateDashboardSlug(pathname);
        const baseRoute = getCorporationDashboardBaseRoute(corporationId);
        if (staticCorporateDashboardSlug) {
            return baseRoute + '/' + staticCorporateDashboardSlug;
        }
        return baseRoute;
    }
    if (pathname.includes(ModuleRoutes.Settings)) {
        return getCorporationSettingsRoute(
            corporationId,
            getTabs(pathname, ModuleRoutes.Settings),
        );
    }
    if (pathname.includes(ModuleRoutes.Map)) {
        return getMapsRoute(corporationId, undefined);
    }
    if (pathname.includes(ModuleRoutes.Hardware)) {
        return resolveClosestHardwareRoute(pathname, { corporationId });
    }

    if (pathname.includes(ModuleRoutes.Reports)) {
        return getCorporationReportsBaseRoute(corporationId);
    }
    return getCorporationLandingPageRoute(corporationId);
};

export const determineGlobalNavigationRoute = (
    pathname: string,
    user?: ICurrentUserResponse,
    permissions?: PermissionsContext,
) => {
    // only keep module context if we are a KCF user with multiple corporations
    if (
        user?.ApplicationRole !== ApplicationRoleCode.User &&
        hasAccessToMultipleCorporations(user)
    ) {
        if (pathname.includes(ModuleRoutes.Settings)) {
            return getGlobalSettingsRoute(getTabs(pathname, ModuleRoutes.Settings));
        }
        if (pathname.includes(ModuleRoutes.Hardware)) {
            const tabs = getTabs(pathname, ModuleRoutes.Hardware);
            const hasLocationOnlyTab =
                tabs?.some((tab) => HARDWARE_TABS_ONLY_AT_LOCATION_LEVEL.includes(tab)) ??
                false;
            if (hasLocationOnlyTab) {
                /*
                 * Location-only tabs (overview, alertLog) don't exist at global scope.
                 * Navigate to the base hardware route so the default tab is selected.
                 */
                return getHardwareRoute(undefined, undefined, undefined);
            }
            return getHardwareRoute(undefined, undefined, tabs);
        }
        if (
            pathname.includes(ModuleRoutes.Reports) &&
            permissions?.canViewGlobalReports !== false
        ) {
            return getGlobalReportsRoute();
        }
    }
    return getOverviewLandingPageRoute();
};

/**
 * Splits the URL to get a list of tabs after main module (determined from the task bar) except a details page
 *
 * @param pathname The entire path or URL
 * @param module The module in the path (e.g. Hardware Management, Settings etc). Module should exist in the path.
 * @returns A list of tabs after the module (does not include a details page tab)
 * @example
 * getTabs('https://v4.sd.kcftech.com/.../settings/notificationSubscriptions/groupView/hardware', ModuleRoutes.Settings) // => ['notificationSubscriptions', 'groupView', 'hardware' ]
 * getTabs('https://v4.sd.kcftech.com/.../hardware/containerManagement/serviceDefinitions/0da1184c-ab29-4e5a-b9b1-7ea74d650d69/containerImages', ModuleRoutes.Hardware) // => ['containerManagement', 'serviceDefinitions' ]
 */
export const getTabs = (pathname: string, module: string) => {
    // This should always execute - the module must be present in the pathname in order to access the remaining tabs
    // If it doesn't exist, this method returns undefined and the navigation defaults to a default tab (if exists) or not found page
    if (pathname.includes(`${module}/`)) {
        const tabsPath = pathname.split(`${module}/`);
        const splitTabs = tabsPath[1].split('/');

        const detailsPageTabIndex = findLastIndex(
            splitTabs,
            (tab) => NumberOrSerialNumberUrlPartRegex.test(tab) || isValidGuid(tab),
        );

        const tabs =
            detailsPageTabIndex !== -1
                ? splitTabs.slice(0, detailsPageTabIndex)
                : splitTabs;

        return tabs.length > 0 ? tabs : undefined;
    }
    return undefined;
};

/**
 * Appends content to end of URL.  If the supplied URL does
 * not end with a slash, one will be added before appending
 * the content.
 */
export function appendToRoute(url: string, ...append: (string | number)[]) {
    const suffix = append.join('/');
    return url.endsWith('/') ? `${url}${suffix}` : `${url}/${suffix}`;
}
